import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_EQUIPMENT_ROUTER, API_ORDER_ROUTER } from "../../../services/api-config";

// ===== Enums =====
export enum EQUIPMENT_TYPE {
  BAG = 1,
  WASHING_MACHINE = 2,
  DRYER = 3,
  IRON = 4,
  FOLDING = 5,
  BASKET = 6,
}

export const EQUIPMENT_TYPE_NAME: Record<number, string> = {
  [EQUIPMENT_TYPE.BAG]: "Túi",
  [EQUIPMENT_TYPE.WASHING_MACHINE]: "Máy giặt",
  [EQUIPMENT_TYPE.DRYER]: "Máy sấy",
  [EQUIPMENT_TYPE.IRON]: "Bàn ủi",
  [EQUIPMENT_TYPE.FOLDING]: "Bàn gấp",
  [EQUIPMENT_TYPE.BASKET]: "Sọt",
};

export enum EQUIPMENT_STATUS {
  AVAILABLE = 1,
  IN_USE = 2,
  MAINTENANCE = 3,
  DECOMMISSIONED = 4,
}

export const EQUIPMENT_STATUS_NAME: Record<number, string> = {
  [EQUIPMENT_STATUS.AVAILABLE]: "Khả dụng",
  [EQUIPMENT_STATUS.IN_USE]: "Đang sử dụng",
  [EQUIPMENT_STATUS.MAINTENANCE]: "Bảo trì",
  [EQUIPMENT_STATUS.DECOMMISSIONED]: "Ngừng sử dụng",
};

// ===== Interfaces =====
export interface Equipment {
  id: number;
  branch_id: number;
  code: string;
  name: string;
  type: EQUIPMENT_TYPE;
  status: EQUIPMENT_STATUS;
  barcode_data?: string;
  current_order_id?: number;
  capacity?: number;
  note?: string;
  created_at: string;
  updated_at: string;
}

export interface EquipmentListFilters {
  branch_id: number;
  type?: number;
  status?: number;
  key_search?: string;
}

export interface EquipmentListResponse {
  list: Equipment[];
}

export interface CreateEquipmentPayload {
  branch_id: number;
  code: string;
  name?: string;
  type: EQUIPMENT_TYPE;
  capacity?: number;
  note?: string;
}

export interface UpdateEquipmentPayload {
  name?: string;
  type?: EQUIPMENT_TYPE;
  status?: EQUIPMENT_STATUS;
  barcode_data?: string;
  capacity?: number;
  note?: string;
}

export interface AssignEquipmentPayload {
  order_id: number;
  weight?: number;
  /** Web (quản lý thao tác): không tự gán người gắn thiết bị vào vai của bước — theo bảng phân công. */
  skip_actor_assignment?: boolean;
}

export interface ScanEquipmentPayload {
  code: string;
  branch_id?: number;
}

export interface ScanResult {
  found: boolean;
  equipment?: Equipment;
  current_order?: any;
  message?: string;
}

export interface OrderEquipmentSummaryItem {
  type: number;
  type_name: string;
  count: number;
  equipment_ids: number[];
  codes: string[];
  names: string[];
}

// Chi tiết TỪNG lượt gắn thiết bị (cả đang gắn lẫn đã trả) — dùng cho tab "Quá trình" (đối soát).
export interface OrderEquipmentDetailItem {
  id: number;
  equipment_id: number;
  assigned_at: string;
  released_at: string | null;
  assigned_by: number | null;
  status: number;
  equipment_code: string;
  equipment_name: string;
  equipment_type: number;
  barcode_data?: string;
}

// ===== Service =====
const EquipmentService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<EquipmentListResponse>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: EquipmentListFilters) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading,
    };
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Equipment>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (payload: CreateEquipmentPayload) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<Equipment>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateEquipmentPayload) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  // Xóa thiết bị — BE chỉ cho xóa khi thiết bị đang trống & chưa từng gắn đơn nào.
  remove: () => {
    const { request, baseResponse, loading } = useFetchData.Post<{ message: string }>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_DELETE(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  assign: () => {
    const { request, baseResponse, loading } = useFetchData.Post<any>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: AssignEquipmentPayload) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_ASSIGN(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  release: () => {
    const { request, baseResponse, loading } = useFetchData.Post<any>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_RELEASE(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  available: () => {
    const { request, baseResponse, loading } = useFetchData.Get<EquipmentListResponse>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: { branch_id: number; type?: number }) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_AVAILABLE,
          data: params,
        }),
      response: baseResponse,
      loading,
    };
  },

  scan: () => {
    const { request, baseResponse, loading } = useFetchData.Post<ScanResult>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (payload: ScanEquipmentPayload) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_SCAN,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  orderEquipmentSummary: () => {
    const { request, baseResponse, loading } = useFetchData.Get<OrderEquipmentSummaryItem[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (orderId: number) =>
        request({
          endPoint: API_EQUIPMENT_ROUTER.API_ORDER_SUMMARY(orderId),
        }),
      response: baseResponse,
      loading,
    };
  },

  // Chi tiết từng lượt gắn thiết bị của đơn (cả đang gắn lẫn đã trả) — cho tab "Quá trình".
  orderEquipmentDetail: () => {
    const { request, baseResponse, loading } = useFetchData.Get<OrderEquipmentDetailItem[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (orderId: number) =>
        request({
          endPoint: API_ORDER_ROUTER.API_ORDER_EQUIPMENTS(orderId),
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default EquipmentService;
